/**
 * Single source of truth della navigazione del portale.
 *
 * Da agosto 2026 la navigazione vive in una **barra laterale** (`<SideRail />`),
 * non più nelle voci dell'header. Decisione di Enrico (12-08), con la diagnosi
 * che l'ha motivata: i moduli M1-M4 **non avevano una voce di navigazione** — ci
 * si arrivava solo da `Progetto → Moduli → clic` o dai link dentro i widget della
 * dashboard. Risultato: la dashboard faceva da navigazione, e per questo sembrava
 * poco funzionale.
 *
 * Tre zone, perché servono a persone diverse:
 *  - **Il lavoro** — Dashboard e i moduli operativi (dove si passa la giornata);
 *  - **Il progetto** — la vetrina per il cliente (Panoramica, Attività, Moduli);
 *  - **Aida** — Memoria (voce a sé «per il momento», parole sue).
 * Admin resta in fondo, staccato, solo per MAITIME_STAFF.
 *
 * Filtraggio (invariato nella sostanza, due tipi di permesso):
 *  - voci di progetto → `nav_voce.{permessoKey}`;
 *  - moduli → `modulo.{codice}`, lo stesso permesso che governa già la rotta e la
 *    pagina `/moduli`. Non sono state inventate chiavi nuove.
 *  - Admin → solo MAITIME_STAFF (ignora i flag).
 *  - Feature flag build-time `VITE_NAV_VISIBLE_ITEMS` (CSV whitelist) come
 *    gating aggiuntivo per ambiente.
 */

import {
  Activity,
  Bell,
  Boxes,
  Brain,
  CalendarCheck,
  CalendarRange,
  ClipboardList,
  Factory,
  FileText,
  FileUp,
  FlaskConical,
  Gauge,
  HelpCircle,
  LayoutDashboard,
  LayoutGrid,
  ListOrdered,
  type LucideIcon,
  PackageCheck,
  Shield,
  SlidersHorizontal,
  Truck,
  Warehouse,
  Wheat,
} from "lucide-react";

import type { PermessiUtente, RisorsaTipo, Utente } from "@/types/api";
import { hasPermesso, hasPermessoSezione } from "@/lib/permessi";

export interface NavItem {
  /** Identificatore stabile (per feature flag CSV e React key). */
  key: string;
  /** Label user-facing (italiano). */
  label: string;
  /** URL path. Opzionale se la voce è solo contenitore di figli. */
  path?: string;
  /** Icona della barra. Obbligatoria di fatto: chiusa si vede solo quella. */
  icon?: LucideIcon;
  /**
   * Tipo di permesso che governa la voce. `nav_voce` per le sezioni di
   * progetto, `modulo` per i moduli operativi. Assente = sempre visibile.
   */
  permessoTipo?: Extract<RisorsaTipo, "nav_voce" | "modulo">;
  /**
   * Chiave del permesso da controllare. Default = `key`. Serve quando la voce ha
   * una `key` distinta dalla chiave-permesso (es. la Panoramica della vetrina ha
   * key "progetto-home" ma permesso "progetto").
   */
  permessoKey?: string;
  /** Voce visibile solo a MAITIME_STAFF (ignora feature flag e permessi). */
  requiresMaitimeStaff?: boolean;
  /**
   * Etichetta minuscola accanto al nome nella barra delle tab: «parziale», «in
   * arrivo». Vive qui e non nelle pagine perché la promessa che una sezione fa
   * deve essere una sola — nella barra e nelle tab si leggeva diversa.
   */
  badge?: string;
  /**
   * Sezione che **non** è una tab: sta dietro un ingranaggio in alto a destra.
   * È il caso delle Impostazioni (decisione di Enrico, 27-08): non sono un posto
   * dove si lavora, sono configurazione, e mettendole in fila con le sezioni di
   * lavoro sembravano una di quelle.
   *
   * Resta però una sezione a tutti gli effetti: la barra la mostra e il permesso
   * `nav_voce.{modulo}.{sezione}` continua a governarla (anche in `/admin`).
   */
  ingranaggio?: boolean;
  /**
   * Sezioni del modulo, mostrate solo a barra aperta. Sono **scorciatoie** che
   * entrano diritte in una sezione: le tab restano dentro il modulo.
   * Regola: la barra serve a spostarsi TRA i moduli, le tab DENTRO un modulo.
   */
  children?: NavItem[];
}

export interface NavZona {
  /** Titolo della zona; assente = zona senza intestazione. */
  titolo?: string;
  voci: NavItem[];
}

/**
 * Le sezioni di M1. Erano due schede dentro un'unica pagina che impilava la
 * lettura d'insieme, i filtri e la tabella; dal 16-08 (richiesta di Enrico)
 * diventano **tre pagine**, ognuna con una domanda sola a cui rispondere:
 * come stiamo messi · cosa guardo adesso · cosa manca e cosa avanza.
 *
 * Nomi di una parola: nella barra la voce padre dà già il contesto.
 * La `key` è anche la chiave-permesso (`nav_voce.m1.*`) ed eredita dal modulo.
 */
export const SEZIONI_M1: NavItem[] = [
  // L'atterraggio è il Cruscotto (scelta di Enrico, 16-08): chi apre Commesse
  // vuole prima sapere come si sta messi, poi entra nel dettaglio.
  { key: "m1.cruscotto", label: "Cruscotto", path: "/moduli/order-readiness", icon: Gauge },
  {
    key: "m1.portafoglio",
    label: "Portafoglio",
    path: "/moduli/order-readiness?tab=portafoglio",
    icon: ClipboardList,
  },
  {
    key: "m1.magazzino",
    label: "Magazzino",
    path: "/moduli/order-readiness?tab=magazzino",
    icon: Warehouse,
  },
  {
    key: "m1.impostazioni",
    label: "Impostazioni",
    path: "/moduli/order-readiness?tab=impostazioni",
    icon: SlidersHorizontal,
    // Come in M2 e M3: le manopole non stanno in fila con le sezioni di lavoro.
    // Qui dentro vive la shelf life, che è la stessa per tutta la piattaforma.
    ingranaggio: true,
  },
];

/**
 * Le sezioni di M2 (17-08). Erano 5 tab + 9 sotto-tab dentro un'unica pagina —
 * quattordici destinazioni, nessuna raggiungibile dalla barra.
 *
 * Ognuna risponde a una domanda sola: come sta il piano · cosa si produce e
 * quando · cosa succede se lo cambio · cosa c'è da produrre · con che regole.
 *
 * «Simulazione» è il what-if, nominato in italiano (scelta di Enrico): tiene sia
 * il riordino proposto dall'AI sia la prova a mano. Resta voce a sé perché il
 * what-if è la funzione che Giacomo userà di più, e da dentro un pulsante del
 * Piano non si sarebbe mai scoperta.
 */
export const SEZIONI_M2: NavItem[] = [
  // Atterraggio sul Cruscotto (Enrico, 17-08), come in Commesse.
  { key: "m2.cruscotto", label: "Cruscotto", path: "/moduli/pianificazione", icon: Gauge },
  {
    key: "m2.piano",
    label: "Piano",
    path: "/moduli/pianificazione?tab=piano",
    icon: ListOrdered,
  },
  // Sezione nuova (14-09-2026): l'MRP diventa l'input del modulo, e chi carica
  // un file deve poter vedere cosa ne è stato di ogni riga. Sta dopo il Piano
  // perché è dove si torna quando un numero non torna, non dove si comincia.
  {
    key: "m2.caricamenti",
    label: "Caricamenti",
    path: "/moduli/pianificazione?tab=caricamenti",
    icon: FileUp,
  },
  {
    key: "m2.simulazione",
    label: "Simulazione",
    path: "/moduli/pianificazione?tab=simulazione",
    icon: FlaskConical,
  },
  {
    key: "m2.fabbisogno",
    label: "Fabbisogno",
    path: "/moduli/pianificazione?tab=fabbisogno",
    icon: Boxes,
  },
  {
    key: "m2.impostazioni",
    label: "Impostazioni",
    path: "/moduli/pianificazione?tab=impostazioni",
    icon: SlidersHorizontal,
    // Fuori dalle tab: le manopole del motore non stanno in fila con le sezioni
    // di lavoro. Resta in barra e resta governata dal suo permesso.
    ingranaggio: true,
  },
];

/**
 * Le sezioni di M4 (18-08). Il modulo è **il flusso dell'impianto**, letto da
 * valle a monte come lo guarda Enzo: camion in uscita → confezionamento →
 * produzione → materie prime. Il Cruscotto mostra la giornata intera, le altre
 * quattro sono uno stadio ciascuna.
 *
 * ⚠️ «Materie prime» è dichiarata **parziale**: non abbiamo l'inventario (nei
 * magazzini c'è solo prodotto finito), abbiamo il giudizio del MES sul piano.
 * Dirlo nella voce è meglio che scoprirlo entrando.
 *
 * Fino a oggi `modulo.M4` era l'unica chiave di modulo che non governava niente:
 * M4 esisteva solo come widget dentro la dashboard.
 */
export const SEZIONI_M4: NavItem[] = [
  { key: "m4.cruscotto", label: "Cruscotto", path: "/moduli/direzione", icon: Gauge },
  {
    key: "m4.uscite",
    label: "Uscite",
    path: "/moduli/direzione?tab=uscite",
    icon: Truck,
  },
  {
    key: "m4.confezionamento",
    label: "Confezionamento",
    path: "/moduli/direzione?tab=confezionamento",
    icon: PackageCheck,
  },
  {
    key: "m4.produzione",
    label: "Produzione",
    path: "/moduli/direzione?tab=produzione",
    icon: Factory,
  },
  {
    key: "m4.materie",
    label: "Materie prime",
    path: "/moduli/direzione?tab=materie",
    icon: Wheat,
    // Non abbiamo l'inventario: abbiamo il giudizio del MES sul piano. Dirlo
    // nella voce è meglio che scoprirlo entrando.
    badge: "parziale",
  },
];

/**
 * Le sezioni di M3. La `key` è anche la chiave-permesso (`nav_voce.m3.*`) ed è
 * quella che compare nella pagina dei permessi sotto il modulo. `risorsa_key` è
 * TEXT libero in DB: le chiavi puntate non richiedono migrazione.
 */
export const SEZIONI_M3: NavItem[] = [
  // «Carichi previsti» prima del calendario (Enrico, 27-08): è il tabellone dove
  // Marina lavora ogni giorno, ed è già il default di `/moduli/spedizioni` —
  // finché la fila partiva dal Calendario, la prima pillola e il contenuto che
  // si apriva erano due cose diverse.
  { key: "m3.carichi", label: "Carichi previsti", path: "/moduli/spedizioni", icon: Truck },
  { key: "m3.calendario", label: "Calendario", path: "/calendario", icon: CalendarCheck },
  { key: "m3.documenti", label: "Documenti", path: "/moduli/spedizioni?tab=documenti", icon: FileText },
  { key: "m3.partenze", label: "Partenze", path: "/partenze", icon: Bell },
  // Le domande di Aida stavano solo in /admin, cioè dove Marina non entra: se
  // servono ad addestrarla, chi la addestra deve poterle vedere (29-08).
  { key: "m3.domande", label: "Domande di Aida", path: "/moduli/spedizioni?tab=domande", icon: HelpCircle },
  // Dal 03-10-2026 non più «in arrivo»: la merce pronta per cliente e le offerte.
  { key: "m3.pronte", label: "Commesse pronte", path: "/moduli/spedizioni?tab=pronte", icon: PackageCheck },
];

export const NAV_ZONE: NavZona[] = [
  {
    titolo: "Il lavoro",
    voci: [
      { key: "dashboard", label: "Dashboard", path: "/", icon: LayoutDashboard },
      {
        key: "m4",
        label: "Direzione",
        path: "/moduli/direzione",
        icon: Gauge,
        permessoTipo: "modulo",
        permessoKey: "M4",
        // Come gli altri: le sezioni ereditano dal modulo, quindi il cambiamento
        // è additivo — chi ha M4 le vede tutte finché nessuno ne spegne una.
        children: SEZIONI_M4,
      },
      {
        key: "m1",
        label: "Commesse",
        path: "/moduli/order-readiness",
        icon: ClipboardList,
        permessoTipo: "modulo",
        permessoKey: "M1",
        children: SEZIONI_M1,
      },
      {
        key: "m2",
        label: "Pianificazione",
        path: "/moduli/pianificazione",
        icon: CalendarRange,
        permessoTipo: "modulo",
        permessoKey: "M2",
        // Come M3: le sezioni hanno un permesso proprio che **eredita dal
        // modulo**, quindi il cambiamento è additivo (chi ha M2 le vede tutte
        // finché qualcuno non ne spegne una).
        children: SEZIONI_M2,
      },
      {
        key: "m3",
        label: "Spedizioni",
        path: "/moduli/spedizioni",
        icon: Truck,
        permessoTipo: "modulo",
        permessoKey: "M3",
        // «Partenze scende sotto Spedizioni» (Enrico, 12-08): resta una rotta
        // corta e memorizzabile perché la guarda il presidente, ma smette di
        // essere una voce di primo livello.
        //
        // Le sezioni hanno un permesso proprio che **eredita dal modulo**: senza
        // override valgono quanto `modulo.M3`, e un `false` esplicito toglie la
        // singola sezione a quel ruolo. È la granularità che chiedeva Enrico
        // («o una sottosezione del modulo»).
        children: SEZIONI_M3,
      },
    ],
  },
  {
    titolo: "Il progetto",
    voci: [
      {
        key: "progetto-home",
        label: "Panoramica",
        path: "/progetto",
        icon: LayoutGrid,
        permessoTipo: "nav_voce",
        permessoKey: "progetto",
      },
      // ⚠️ **Bacheca, Documenti e Conversazioni sono uscite dalla barra** il
      // 21-08-2026 (decisione di Enrico): al cliente restano Panoramica,
      // Attività e Moduli.
      //
      // Le rotte, i permessi e il backoffice **restano tutti in piedi**: qui è
      // sparita solo la porta. Rimetterle è cancellare questo commento e
      // riscrivere tre voci — per questo non ho tolto altro.
      {
        key: "attivita",
        label: "Attività",
        path: "/attivita",
        icon: Activity,
        permessoTipo: "nav_voce",
      },
      {
        key: "moduli",
        label: "Moduli",
        path: "/moduli",
        icon: LayoutGrid,
        permessoTipo: "nav_voce",
      },
    ],
  },
];

/**
 * La zona **Agenti**: voce di primo livello col cervello, e sotto i nomi degli
 * agenti (decisione di Enrico, 12-08 — prima era una zona «Aida» con dentro
 * Memoria).
 *
 * È una voce **dinamica**: i figli non vengono da qui ma dal **roster**
 * (`/api/v1/agenti`), così un agente futuro compare da sé senza toccare la
 * navigazione. Qui c'è solo cosa un agente espone all'utente.
 */
export const NAV_AGENTI = { key: "agenti", label: "Agenti", icon: Brain };

/** Le sezioni che ogni agente espone lato utente. Un agente senza sezioni non
 *  ha una pagina propria: nella barra resta un'intestazione (o, per lo staff,
 *  porta alla sua vigilanza in Admin). */
export const SEZIONI_AGENTE: Record<string, NavItem[]> = {
  aida: [{ key: "memoria", label: "Memoria", path: "/memoria" }],
};

/** Admin: zona propria, in fondo, staccata. Le sue 7 famiglie e 16 pagine
 *  arrivano da `admin-nav.ts` e si mostrano a barra aperta. */
export const NAV_ADMIN: NavItem = {
  key: "admin",
  label: "Admin",
  path: "/admin",
  icon: Shield,
  requiresMaitimeStaff: true,
};

export function isMaitimeStaff(user: Utente | null): boolean {
  return user?.ruolo === "maitime_staff";
}

function readVisibleKeysFromEnv(): Set<string> | null {
  const raw: string = (import.meta.env.VITE_NAV_VISIBLE_ITEMS ?? "").trim();
  if (!raw) return null;
  const keys = raw
    .split(",")
    .map((s: string) => s.trim())
    .filter(Boolean);
  return keys.length > 0 ? new Set(keys) : null;
}

/** Le zone visibili a questo utente, già filtrate e senza zone rimaste vuote.
 *
 * ⚠️ **L'atterraggio non è più scritto qui.** Fino al 30-08-2026 questa funzione
 * conteneva due elenchi di ruoli a mano — `voceAtterraggio` (che aspetto ha la
 * prima voce) e `atterraSuUnModulo` (se sparisce) — gemelli di due `if` in
 * `DashboardLanding.tsx`. Concordavano per disciplina, non per costruzione.
 * Adesso arriva tutto da `perms.atterraggio`, cioè da `auth.ruolo_atterraggio`,
 * e la barra e il redirect di `/` leggono **la stessa risposta**.
 */
export function getVisibleZone(
  user: Utente | null,
  perms: PermessiUtente | undefined,
): NavZona[] {
  const staff = isMaitimeStaff(user);
  const allowedKeys = readVisibleKeysFromEnv();
  const atterraggio = perms?.atterraggio ?? { path: "/", sezioni_in_cima: null };

  const visibile = (item: NavItem): boolean => {
    if (item.requiresMaitimeStaff) return staff;
    if (allowedKeys && !allowedKeys.has(item.key)) return false;
    if (!item.permessoTipo) return true;
    if (staff) return true;
    return hasPermesso(perms, item.permessoTipo, item.permessoKey ?? item.key);
  };

  /** Le sezioni visibili di un modulo, e il punto in cui il modulo atterra.
   *
   *  ⚠️ Qui c'era anche un filtro che **toglieva** dal modulo la sezione usata
   *  come voce di atterraggio — serviva a non avere due porte per la stessa
   *  stanza. Dal 30-08 la voce di atterraggio non esiste più (scelta di Enrico:
   *  «nessuna voce di atterraggio»), quindi quel filtro renderebbe la sezione
   *  irraggiungibile invece che non-doppia: il calendario di Marina è tornato
   *  dentro Spedizioni, dov'è di casa. */
  const conSezioni = (item: NavItem): NavItem => {
    if (!item.children?.length) return item;
    const moduloKey = item.permessoKey ?? item.key;
    const visibili = item.children.filter((c) =>
      hasPermessoSezione(perms, c.key, moduloKey),
    );
    return { ...item, children: visibili, path: visibili[0]?.path ?? item.path };
  };

  const zone = NAV_ZONE.map((z) => ({
    ...z,
    voci: z.voci
      .filter(visibile)
      // La Dashboard si mostra **a chi sulla Dashboard ci atterra davvero**.
      // Per chi atterra su un modulo sarebbe una seconda porta per una stanza
      // in cui è già entrato; per Marina, che atterra sul calendario, sarebbe
      // una pagina che non usa. È la stessa regola di prima, ma dedotta dal
      // dato invece che da un elenco di ruoli.
      .filter((v) => !(v.key === "dashboard" && atterraggio.path !== "/"))
      .map(conSezioni)
      // Un modulo a cui sono state tolte TUTTE le sezioni non ha più niente da
      // aprire: sparisce, invece di portare a una pagina vietata.
      .filter((v) => !v.children || v.children.length > 0),
  })).filter((z) => z.voci.length > 0);

  return promuoviSezioni(zone, atterraggio.sezioni_in_cima);
}

/**
 * Le sezioni del modulo indicato **salgono al primo livello**, al posto della
 * voce del modulo.
 *
 * La regola c'era già, ma si misurava sul **numero** di moduli visibili («chi ne
 * vede uno solo»). Sembrava innocua e non lo era: il giorno in cui a Marina è
 * stato dato `modulo.M1`, la regola ha smesso di scattare e le sue cinque
 * sezioni di Spedizioni — le icone su cui passa la giornata — sono diventate
 * figli annidati, visibili solo aprendo la barra. Nessuno l'aveva deciso: è
 * stato l'effetto collaterale di un permesso.
 *
 * Ora è **dichiarata** in `auth.ruolo_atterraggio.sezioni_in_cima` e si cambia
 * dal pannello admin, quindi concedere un modulo in più non riscrive la barra di
 * qualcuno alle sue spalle.
 */
function promuoviSezioni(zone: NavZona[], moduloCodice: string | null): NavZona[] {
  if (!moduloCodice) return zone;
  return zone.map((z) => {
    const promosse = z.voci.flatMap((v) =>
      v.permessoTipo === "modulo" && (v.permessoKey ?? v.key) === moduloCodice
        ? (v.children ?? [])
        : [],
    );
    if (!promosse.length) return z;
    const resto = z.voci.filter(
      (v) => !(v.permessoTipo === "modulo" && (v.permessoKey ?? v.key) === moduloCodice),
    );
    // ⚠️ **In cima alla zona, non al posto del modulo.** Lasciandole dov'era il
    // modulo, l'ordine di `NAV_ZONE` decideva: M3 viene dopo M1, quindi a Marina
    // il Portafoglio finiva sopra il tabellone su cui passa la giornata. Il
    // modulo promosso è quello dove la persona vive — la barra diventa il suo
    // menù di lavoro, e un menù di lavoro comincia dal lavoro.
    return { ...z, voci: [...promosse, ...resto] };
  });
}

/** Tutte le voci visibili in fila, figli compresi: serve a chi deve risolvere
 *  una rotta in un titolo di sezione. */
export function vociPiatte(zone: NavZona[]): NavItem[] {
  return zone.flatMap((z) => z.voci.flatMap((v) => [v, ...(v.children ?? [])]));
}

/**
 * Il **catalogo delle destinazioni** di atterraggio, per il pannello admin.
 *
 * Nasce da `NAV_ZONE`, cioè dalla stessa lista che disegna la barra: è il
 * principio «catalogo in codice / visibilità in configurazione». Il backend
 * salva una rotta e basta — tenere una seconda copia di questo elenco in DB
 * sarebbe la lista da allineare a mano, che è il difetto che la tabella
 * `auth.ruolo_atterraggio` esiste per chiudere.
 */
export interface Destinazione {
  path: string;
  label: string;
  /** Il modulo che la contiene, se è la sezione di un modulo: serve al pannello
   *  per avvisare quando si manda un ruolo dove non ha accesso. */
  moduloKey?: string;
  /** Chiave-permesso della voce (`nav_voce.*` o il codice modulo). */
  permessoKey?: string;
  permessoTipo?: Extract<RisorsaTipo, "nav_voce" | "modulo">;
}

export function destinazioniAtterraggio(): Destinazione[] {
  const out: Destinazione[] = [];
  for (const zona of NAV_ZONE) {
    for (const voce of zona.voci) {
      if (voce.path) {
        out.push({
          path: voce.path,
          label: voce.label,
          permessoKey: voce.permessoKey ?? voce.key,
          permessoTipo: voce.permessoTipo,
        });
      }
      for (const figlio of voce.children ?? []) {
        // Le Impostazioni stanno dietro un ingranaggio, non sono un posto dove
        // si lavora: mandarci qualcuno ad atterrare non ha senso.
        if (figlio.ingranaggio || !figlio.path) continue;
        out.push({
          path: figlio.path,
          label: `${voce.label} → ${figlio.label}`,
          moduloKey: voce.permessoKey ?? voce.key,
          permessoKey: figlio.key,
          permessoTipo: "nav_voce",
        });
      }
    }
  }
  // La pagina per chi non ha ancora un posto dove lavorare. Non è in NAV_ZONE
  // perché non è una voce di barra, ma è una destinazione legittima: è dove
  // atterrano oggi viewer e commerciale.
  out.push({ path: "/non-disponibile", label: "Nessun accesso operativo" });
  // Deduplica sul path tenendo la prima etichetta: `/calendario` compare sia
  // come sezione di M3 sia come rotta corta.
  const visti = new Set<string>();
  return out.filter((d) => (visti.has(d.path) ? false : (visti.add(d.path), true)));
}

/** I moduli le cui sezioni si possono promuovere in cima. Gemello di
 *  `MODULI_VALIDI` in `core/auth/atterraggio.py`. */
export function moduliPromuovibili(): { codice: string; label: string }[] {
  return NAV_ZONE.flatMap((z) =>
    z.voci
      .filter((v) => v.permessoTipo === "modulo" && v.children?.length)
      .map((v) => ({ codice: v.permessoKey ?? v.key, label: v.label })),
  );
}
